import { useState } from 'react';
import { BoutonMint, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { ErreurApi } from '../../lib/api';
import { lireSeance, type SeanceAffichee } from '../../lib/useEmargement';
import { useDemanderModification } from '../../lib/useModificationsSensibles';
import {
  AvisValidation,
  ChampMotif,
  DemandeEnCours,
  motifValide,
  useValidationDirecte,
} from '../modifications-sensibles/ElementsDemande';

/**
 * Réouverture d'une feuille clôturée (TF-437) : une modification sensible.
 * Le pôle la demande à la direction, qui l'approuve ; la direction rouvre
 * elle-même. Dans les deux cas, motif exigé, et rien n'est retiré de ce qui a
 * été signé : le PV archivé reste, la prochaine clôture en produit un nouveau.
 */
export function ReouvertureFeuille({
  seance,
  onTermine,
  onAnnuler,
}: {
  seance: Pick<SeanceAffichee, 'id' | 'reouvertureEnAttente'>;
  /** La feuille relue après le geste, et ce qu'il a fait. */
  onTermine: (seance: SeanceAffichee, message: string) => void;
  onAnnuler: () => void;
}): JSX.Element {
  const directe = useValidationDirecte();
  const demander = useDemanderModification();
  const [motif, setMotif] = useState('');
  const [relecture, setRelecture] = useState(false);

  async function relire(message: string): Promise<void> {
    setRelecture(true);
    try {
      onTermine(await lireSeance(seance.id), message);
    } finally {
      setRelecture(false);
    }
  }

  if (seance.reouvertureEnAttente) {
    return (
      <div className="space-y-3">
        <DemandeEnCours
          demande={seance.reouvertureEnAttente}
          onRetiree={() => void relire('Demande de réouverture retirée.')}
        />
        <BoutonSecondaire onClick={onAnnuler}>Fermer</BoutonSecondaire>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-codialis-gray">
        {directe
          ? 'La feuille redevient modifiable. Aucune signature n’est supprimée ; le PV archivé reste consultable, et la prochaine clôture en produira un nouveau.'
          : 'La direction décide de la réouverture. Rouverte, la feuille redevient modifiable ; aucune signature n’est supprimée. Pour corriger un seul apprenant, demandez plutôt la correction depuis son détail.'}
      </p>
      <AvisValidation directe={directe} />
      <ChampMotif
        valeur={motif}
        onChange={setMotif}
        directe={directe}
        placeholder="Signature du formateur à reprendre, appel à refaire…"
      />
      {demander.error && (
        <MessageErreur
          message={demander.error instanceof ErreurApi ? demander.error.message : 'La demande n’a pas pu être envoyée.'}
        />
      )}
      <div className="flex flex-wrap gap-2">
        <BoutonMint
          disabled={demander.isPending || relecture || !motifValide(motif)}
          onClick={() =>
            demander.mutate(
              { type: 'REOUVERTURE_FEUILLE', seanceId: seance.id, motif: motif.trim() },
              { onSuccess: (resultat) => void relire(resultat.message) },
            )
          }
        >
          {demander.isPending || relecture ? 'Envoi…' : directe ? 'Rouvrir l’émargement' : 'Demander la réouverture'}
        </BoutonMint>
        <BoutonSecondaire onClick={onAnnuler}>Annuler</BoutonSecondaire>
      </div>
    </div>
  );
}
